import React from "react";
import { Loader2, Save, X, Image as ImageIcon } from "lucide-react";
import { slugify } from "./_shared";
import { useT } from "@/lib/LanguageContext";
import LanguageAwareField from "../LanguageAwareField";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { cn } from "../../lib/utils";

/**
 * `error` is the message ALREADY RESOLVED by the caller's `t` (state holds an i18n key —
 * see lib/validation.js).
 */
const Field = ({ label, value, onChange, multiline, rows = 3, testid, error }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{label}</label>
    {multiline ? (
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={cn(
          "mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-4 py-2.5 text-eh-ink focus:outline-none focus:border-eh-sage-deep resize-none font-body text-sm leading-relaxed",
          error && invalidInputClass,
        )}
        data-testid={testid}
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={cn(
          "mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-4 py-2.5 text-eh-ink focus:outline-none focus:border-eh-sage-deep font-body text-sm",
          error && invalidInputClass,
        )}
        data-testid={testid}
      />
    )}
    <FieldError message={error} testId={testid ? `${testid}-error` : undefined} />
  </div>
);

/**
 * `fieldErrors` holds i18n KEYS (hooks/useFieldErrors.js); this component resolves them
 * through its own `t`, which is what makes the same error map correct in three locales.
 */
export default function BlogEditor({ editing, form, setForm, busy, fieldErrors = {}, clearFieldError = () => {}, onSave, onCancel, onUploadCover }) {
  const t = useT("admin");
  const tv = useT("common");
  // The title pair shares ONE input, so it gets ONE message slot: withAnyLangRule reports
  // "at least one language" on title_fr, and either language typed is what fixes it — hence
  // both keys cleared on every keystroke.
  const titleError = fieldErrors.title_fr || fieldErrors.title_en;
  const setTitle = (lang) => (v) => {
    setForm((f) => ({ ...f, [`title_${lang}`]: v, slug: f.slug || slugify(v) }));
    clearFieldError("title_fr");
    clearFieldError("title_en");
    clearFieldError("slug"); // the slug this keystroke just derived
  };
  return (
    <div className="mt-6 bg-eh-cream border border-eh-border-soft rounded-3xl p-8" data-testid="admin-blog-editor">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-eh-ink">{editing === "new" ? t("blog.newTitle") : t("blog.editTitle")}</h2>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="btn-ghost text-sm" data-testid="admin-blog-cancel"><X size={16} /> {t("bookings.cancel")}</button>
          <button onClick={onSave} disabled={busy} className="btn-primary text-sm disabled:opacity-60" data-testid="admin-blog-save">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {t("actions.save")}
          </button>
        </div>
      </div>

      <div className="space-y-5">
        <LanguageAwareField
          label={t("blog.field.title", null, "Title")}
          valueFr={form.title_fr}
          valueEn={form.title_en}
          onChangeFr={setTitle("fr")}
          onChangeEn={setTitle("en")}
          requiredLang="any"
          error={titleError ? tv(titleError) : ""}
          testid="post-title"
        />
        <LanguageAwareField
          label={t("blog.field.tag", null, "Tag / Category")}
          valueFr={form.tag_fr}
          valueEn={form.tag_en}
          onChangeFr={(v) => setForm((f) => ({ ...f, tag_fr: v }))}
          onChangeEn={(v) => setForm((f) => ({ ...f, tag_en: v }))}
          testid="post-tag"
        />
        <LanguageAwareField
          label={t("blog.field.excerpt", null, "Excerpt")}
          valueFr={form.excerpt_fr}
          valueEn={form.excerpt_en}
          onChangeFr={(v) => setForm((f) => ({ ...f, excerpt_fr: v }))}
          onChangeEn={(v) => setForm((f) => ({ ...f, excerpt_en: v }))}
          multiline
          rows={3}
          testid="post-excerpt"
        />
        <LanguageAwareField
          label={t("blog.field.body", null, "Post Body")}
          valueFr={form.body_fr}
          valueEn={form.body_en}
          onChangeFr={(v) => setForm((f) => ({ ...f, body_fr: v }))}
          onChangeEn={(v) => setForm((f) => ({ ...f, body_en: v }))}
          multiline
          rows={10}
          testid="post-body"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-5">
        <Field
          label={t("blog.field.slug")}
          value={form.slug}
          onChange={(v) => { setForm((f) => ({ ...f, slug: slugify(v) })); clearFieldError("slug"); }}
          testid="post-slug"
          error={fieldErrors.slug ? tv(fieldErrors.slug) : ""}
        />
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("blog.field.cover")}</label>
          <div className="mt-2 flex items-center gap-3">
            {form.cover_url ? (
              <img src={form.cover_url} alt="" className="w-16 h-16 rounded-xl object-cover border border-eh-border-soft" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-eh-bg flex items-center justify-center text-eh-muted">
                <ImageIcon size={18} />
              </div>
            )}
            <label className="btn-ghost text-xs cursor-pointer">
              {t("blog.upload")}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && onUploadCover(e.target.files[0])}
                data-testid="post-cover-upload"
              />
            </label>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("blog.field.status")}</label>
          <label className="mt-2 flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
              data-testid="post-published-toggle"
              className="w-4 h-4 accent-eh-sage-deep"
            />
            <span className="text-sm text-eh-ink">{form.published ? t("blog.published") : t("blog.draft")}</span>
          </label>
        </div>
      </div>
    </div>
  );
}
